import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import ContactModel, { ContactPayload } from "../../Models/ContactModel";
import config from "../../Utils/Config";

interface ContactsState {
	value: ContactModel[];
	status: "idle" | "loading" | "succeeded" | "failed";
	error: string | null;
}

const initialState: ContactsState = {
	value: [],
	status: "idle",
	error: null,
};

function getApiError(error: unknown): string {
	if (!axios.isAxiosError(error)) {
		return "אירעה שגיאה לא צפויה";
	}

	const data = (error as AxiosError<Record<string, string[] | string>>).response?.data;
	if (!data) {
		return "לא ניתן להתחבר לשרת";
	}

	return Object.values(data)
		.flatMap((message) => Array.isArray(message) ? message : [message])
		.join(" ");
}

export const fetchContacts = createAsyncThunk<
	ContactModel[],
	void,
	{ rejectValue: string }
>("contacts/fetchContacts", async (_, { rejectWithValue }) => {
	try {
		const response = await axios.get<ContactModel[]>(config.contactsUrl);
		return response.data;
	} catch (error) {
		return rejectWithValue(getApiError(error));
	}
});

export const addContact = createAsyncThunk<
	ContactModel,
	ContactPayload,
	{ rejectValue: string }
>("contacts/addContact", async (contact, { rejectWithValue }) => {
	try {
		const response = await axios.post<ContactModel>(config.contactsUrl, contact);
		return response.data;
	} catch (error) {
		return rejectWithValue(getApiError(error));
	}
});

export const updateContact = createAsyncThunk<
	ContactModel,
	{ id: number; contact: ContactPayload },
	{ rejectValue: string }
>("contacts/updateContact", async ({ id, contact }, { rejectWithValue }) => {
	try {
		const response = await axios.put<ContactModel>(
			`${config.contactsUrl}${id}`,
			contact,
		);
		return response.data;
	} catch (error) {
		return rejectWithValue(getApiError(error));
	}
});

export const deleteContact = createAsyncThunk<
	number,
	number,
	{ rejectValue: string }
>("contacts/deleteContact", async (id, { rejectWithValue }) => {
	try {
		await axios.delete(`${config.contactsUrl}${id}`);
		return id;
	} catch (error) {
		return rejectWithValue(getApiError(error));
	}
});

const contactsSlicer = createSlice({
	name: "contacts",
	initialState,
	reducers: {
		clearContactsError(state) {
			state.error = null;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchContacts.pending, (state) => {
				state.status = "loading";
				state.error = null;
			})
			.addCase(fetchContacts.fulfilled, (state, action) => {
				state.status = "succeeded";
				state.value = action.payload;
			})
			.addCase(fetchContacts.rejected, (state, action) => {
				state.status = "failed";
				state.error = action.payload ?? "טעינת אנשי הקשר נכשלה";
			})
			.addCase(addContact.fulfilled, (state, action) => {
				state.value.push(action.payload);
				state.error = null;
			})
			.addCase(addContact.rejected, (state, action) => {
				state.error = action.payload ?? "הוספת איש הקשר נכשלה";
			})
			.addCase(updateContact.fulfilled, (state, action) => {
				const index = state.value.findIndex(
					(contact) => contact.id === action.payload.id,
				);
				if (index !== -1) {
					state.value[index] = action.payload;
				}
				state.error = null;
			})
			.addCase(updateContact.rejected, (state, action) => {
				state.error = action.payload ?? "עדכון איש הקשר נכשל";
			})
			.addCase(deleteContact.fulfilled, (state, action) => {
				state.value = state.value.filter(
					(contact) => contact.id !== action.payload,
				);
				state.error = null;
			})
			.addCase(deleteContact.rejected, (state, action) => {
				state.error = action.payload ?? "מחיקת איש הקשר נכשלה";
			});
	},
});

export const { clearContactsError } = contactsSlicer.actions;
export default contactsSlicer.reducer;
